// Trigger CI to pick up latest rocket-frontend-automation E2E fixes on perf-app-1.
var _getCurrentApiHost;
if (!process.env.REACT_APP_OKTA_ISSUER_DOMAIN || !process.env.REACT_APP_PUSHER_CLIENT_KEY || !process.env.REACT_APP_INTERCOM_APP_ID || !process.env.REACT_APP_PUSHER_CLUSTER || !process.env.REACT_APP_PDFTRON_KEY || !process.env.REACT_APP_ASSETS_HOST || !process.env.REACT_APP_PDFTRON_KEY || !process.env.REACT_APP_ASSETS_HOST) {
    throw new Error('Ensure the env variables are setup, as stated in the README.md file.');
}
/** *
 * This method constructs the api host
 * Checks for current window location path and constructs the api path in a way as:
 *    {protocol}://{subdomain}.api.{domain}
 *    Ex: https://demo.api.nitro.run
 *
 */ /** Desktop shell UA marker — set on the Tauri window in src-tauri/tauri.conf.json */ const DESKTOP_APP_USER_AGENT_RE = /RocketlaneDesktop/i;
const DESKTOP_APP_API_HOST = 'https://api.rocketlane.com';
/** Must match `DESKTOP_API_HOST_KEY` in services/desktop-session.ts (avoid circular import). */ const DESKTOP_STORED_API_HOST_KEY = '__desktop_api_host';
const isDesktopAppUserAgent = ()=>{
    if (typeof navigator === 'undefined') {
        return false;
    }
    return DESKTOP_APP_USER_AGENT_RE.test(navigator.userAgent);
};
const getDesktopApiHost = ()=>{
    if (typeof localStorage !== 'undefined') {
        try {
            const stored = localStorage.getItem(DESKTOP_STORED_API_HOST_KEY);
            if (stored) {
                return stored.replace(/\/$/, '');
            }
        } catch (unused) {
        // ignore storage access errors
        }
    }
    return DESKTOP_APP_API_HOST;
};
const getApiHost = (fallback = process.env.REACT_APP_API_HOST)=>{
    if (isDesktopAppUserAgent()) {
        return getDesktopApiHost();
    }
    const { hostname, protocol } = window.location;
    if (hostname === 'localhost') {
        return fallback;
    }
    const domainArr = hostname.split('.');
    const subdomain = domainArr[0];
    const domain = domainArr[1];
    if (process.env.PLAYWRIGHT_TESTS) {
        const subdomainFromLocalStorage = window.localStorage.getItem('playwright_host_api');
        if (!subdomainFromLocalStorage) {
            return fallback;
        }
        return hostname.replace(subdomain, `${protocol}//${subdomainFromLocalStorage}.api`);
    }
    if (hostname === null || hostname === void 0 ? void 0 : hostname.includes('canary-perf-preprod.nitro.run')) {
        return 'https://nicex-performance-sandbox.api.rocketlane.com';
    }
    if (hostname === null || hostname === void 0 ? void 0 : hostname.includes('rl-template-agent')) {
        return 'https://gourav.api.nitro.run';
    }
    if (hostname === null || hostname === void 0 ? void 0 : hostname.includes('filter-fix')) {
        return 'https://northstar.api.nitro.run';
    }
    if (hostname === null || hostname === void 0 ? void 0 : hostname.includes('highradius-demo')) {
        return 'https://highradius.api.rocketlane.com';
    }
    if (hostname === null || hostname === void 0 ? void 0 : hostname.includes('sprinklr-demo')) {
        return 'https://sprinklr.api.rocketlane.com';
    }
    if (hostname === null || hostname === void 0 ? void 0 : hostname.includes('sprinklr-prod-new')) {
        return 'https://sprinklr.api.rocketlane.com';
    }
    if (subdomain === 'app' && [
        'nitro',
        'rocketlane'
    ].includes(domain)) {
        return hostname.replace(subdomain, `${protocol}//api`);
    }
    return hostname.replace(subdomain, `${protocol}//${subdomain}.api`);
};
// done because for mobile meta alone we need to send 'app', but for other requests we dont
const getApiHostForMobileMeta = (fallback = process.env.REACT_APP_API_HOST)=>{
    if (isDesktopAppUserAgent()) {
        return getDesktopApiHost();
    }
    const { hostname, protocol } = window.location;
    if (hostname === 'localhost' || process.env.PLAYWRIGHT_TESTS) {
        return fallback;
    }
    const subdomain = hostname.split('.')[0];
    return hostname.replace(subdomain, `${protocol}//${subdomain}.api`);
};
// Current API host (starts with .api subdomain); get/set for cookies update
const apiHostState = (()=>{
    let value = getApiHost();
    return {
        get current () {
            return value;
        },
        set current (_trigger){
            let hostname;
            let protocol;
            if (window.location.hostname === 'localhost') {
                const apiHost = value;
                if (!apiHost) {
                    return;
                }
                try {
                    const apiUrl = new URL(apiHost);
                    hostname = apiUrl.hostname;
                    protocol = apiUrl.protocol;
                } catch (unused) {
                    return;
                }
                if (hostname === 'localhost' || hostname === '127.0.0.1') {
                    return;
                }
            } else {
                hostname = window.location.hostname;
                protocol = window.location.protocol;
            }
            const subdomain = hostname.split('.')[0];
            const hostWithoutApi = hostname.includes('.api.') ? hostname.replace('.api.', '.') : hostname;
            value = hostWithoutApi.replace(subdomain, `${protocol}//${subdomain}`);
        }
    };
})();
/**
 * Get the current API host
 * Initially returns host with .api subdomain, can be updated after cookiesEnabled check.
 * Desktop (Tauri): prefer tenant host from `__desktop_api_host` (set from deep-link
 * `account=`), else fall back to central api.rocketlane.com.
 */ export const getCurrentApiHost = ()=>{
    if (isDesktopAppUserAgent()) {
        return getDesktopApiHost();
    }
    return apiHostState.current;
};
/**
 * Update API host to remove .api subdomain when cookies are enabled
 * This allows subsequent API calls to use the subdomain directly (without .api)
 * When on localhost, derive from currentApiHost (production backend) so we still strip .api.
 */ export const updateApiHostForCookies = ()=>{
    apiHostState.current = true;
};
export const NITRO_OAUTH_HOST = ((_getCurrentApiHost = getCurrentApiHost()) === null || _getCurrentApiHost === void 0 ? void 0 : _getCurrentApiHost.includes('nitro.run')) ? 'https://id.nitro.run' : 'https://id.rocketlane.com';
export const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER;
export const PUSHER_CLIENT_KEY = process.env.REACT_APP_PUSHER_CLIENT_KEY;
export const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID;
export const API_HOST_FOR_MOBILE_META = getApiHostForMobileMeta();
export const API_VERSION = 'v1';
export const API_NAMESPACE = 'api';
export const API_VERSION_V2 = 'v2';
export const OKTA_ACCESS_TOKEN_EXPIRY_WINDOW_IN_MINUTES = 15;
export const OKTA_ACCESS_TOKEN_SAFE_WINDOW_IN_MINUTES = 5;
export const OKTA_ACCESS_TOKEN_CHECK_WINDOW_IN_SECONDS = 10;
export const OKTA_ISSUER_DOMAIN = process.env.REACT_APP_OKTA_ISSUER_DOMAIN;
/**
 * Get clientRegion from localStorage accountMeta
 * The accountMeta is fetched via getInitData() (/home/init) and cached in localStorage
 * Returns 'eu' if clientRegion is 'eu', 'us' if 'us', or undefined if not available
 *
 * This function can be reused throughout the app for regional configuration
 */ export const getClientRegion = ()=>{
    try {
        const accountMetaStr = localStorage.getItem('accountMeta');
        if (accountMetaStr) {
            const accountMeta = JSON.parse(accountMetaStr);
            const region = accountMeta === null || accountMeta === void 0 ? void 0 : accountMeta.clientRegion;
            return region;
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[getClientRegion] Failed to parse accountMeta:', error);
    }
    return undefined;
};
export const getConversationServiceUrl = (apiHost)=>{
    const clientRegion = getClientRegion();
    const host = apiHost !== null && apiHost !== void 0 ? apiHost : getCurrentApiHost();
    if (clientRegion === 'eu') {
        return 'https://conversation-service.rl-platforms-eu.rocketlane.com';
    }
    if (host === null || host === void 0 ? void 0 : host.includes('.nitro.run')) {
        return 'https://conversation-service-preprod.rl-platforms.rocketlane.com';
    }
    return 'https://conversation-service.rl-platforms.rocketlane.com';
};
export const CONVERSATION_SERVICE_URL = getConversationServiceUrl();
export const REACT_APP_CONVERSATION_SERVICE_URL = process.env.REACT_APP_CONVERSATION_SERVICE_URL;
/**
 * Cookie mode strips `.api.` from getCurrentApiHost().
 * ChatController is only reachable on the `.api.` host, so restore it.
 * Hosts already on `api.*` or containing `.api.` are left unchanged.
 */ export const ensureApiSubdomain = (host)=>{
    const trimmed = host.replace(/\/$/, '');
    if (trimmed.includes('.api.')) {
        return trimmed;
    }
    try {
        const url = new URL(trimmed);
        const [subdomain, ...rest] = url.hostname.split('.');
        // e.g. api.rocketlane.com — do not rewrite to api.api.rocketlane.com
        if (subdomain === 'api') {
            return url.origin;
        }
        // Match getApiHost(): app.rocketlane.com / app.nitro.run → api.<domain>
        // (not app.api.<domain>). Cookie mode can leave the host as app.* again.
        if (subdomain === 'app' && rest.length > 0 && [
            'rocketlane',
            'nitro'
        ].includes(rest[0])) {
            url.hostname = `api.${rest.join('.')}`;
            return url.origin;
        }
        if (rest.length > 0 && subdomain) {
            url.hostname = `${subdomain}.api.${rest.join('.')}`;
        }
        return url.origin;
    } catch (unused) {
        if (/^https?:\/\/api\./i.test(trimmed)) {
            return trimmed;
        }
        // Same app → api special case for non-URL strings
        const appCanonical = trimmed.replace(/^(https?:\/\/)app\.(rocketlane|nitro)\./i, '$1api.$2.');
        if (appCanonical !== trimmed) {
            return appCanonical;
        }
        return trimmed.replace(/^(https?:\/\/)([^.]+)\./, '$1$2.api.');
    }
};
/**
 * Local / CI direct CS host for entity-agent chat clients.
 * Does **not** change `getConversationServiceUrl()` / `CONVERSATION_SERVICE_URL`
 * used by existing AI chat / policies / RM — those stay on regional URLs.
 */ export const resolveDirectConversationServiceUrl = ()=>{
    const fromEnv = REACT_APP_CONVERSATION_SERVICE_URL;
    if (fromEnv) {
        return fromEnv.replace(/\/$/, '');
    }
    if (typeof localStorage !== 'undefined') {
        try {
            const stored = localStorage.getItem('CONVERSATION_SERVICE_URL');
            if (stored) {
                return stored.replace(/\/$/, '');
            }
        } catch (unused) {
        // SecurityError / blocked storage — fall through
        }
    }
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        return 'http://localhost:8000';
    }
    return null;
};
/** `/api/v1` base for chat HTTP — direct local CS when set, else rocket-api. */ export const getChatApiBaseUrl = ()=>{
    var _getCurrentApiHost;
    const directCs = resolveDirectConversationServiceUrl();
    if (directCs) {
        return `${directCs}/api/v1`;
    }
    return `${ensureApiSubdomain((_getCurrentApiHost = getCurrentApiHost()) !== null && _getCurrentApiHost !== void 0 ? _getCurrentApiHost : '')}/api/v1`;
};
/** Local CS may omit /status; never treat prod 404 as upload success. */ export const isLocalChatApiHost = (baseUrl)=>{
    try {
        const { hostname } = new URL(baseUrl);
        return hostname === 'localhost' || hostname === '127.0.0.1';
    } catch (unused) {
        return false;
    }
};
/**
 * Get the Agents app host (agent-builder / signals) from localStorage accountMeta.
 * The backend resolves the per-region host (us → agents.rocketlane.com,
 * eu → agents-eu.rocketlane.com) and returns it via getInitData() (/home/init),
 * so the frontend does not need to hardcode the region→URL mapping.
 * Returns undefined when accountMeta is not yet loaded.
 */ export const getAgentsHostFromMeta = ()=>{
    try {
        const accountMetaStr = localStorage.getItem('accountMeta');
        if (accountMetaStr) {
            const accountMeta = JSON.parse(accountMetaStr);
            return accountMeta === null || accountMeta === void 0 ? void 0 : accountMeta.agentsHost;
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[getAgentsHostFromMeta] Failed to parse accountMeta:', error);
    }
    return undefined;
};
// Regional Marketplace URLs
// Uses clientRegion from accountMeta which is fetched via getInitData() (/home/init) and cached in localStorage
const getMarketplaceUrls = ()=>{
    // Get clientRegion using the reusable function
    const clientRegion = getClientRegion();
    // Route based on clientRegion from backend
    if (clientRegion === 'eu') {
        return {
            frontend: 'https://assets.marketplace.eu.rocketlane.com',
            backend: 'https://api.marketplace.eu.rocketlane.com'
        };
    }
    // Fallback to hostname check if accountMeta is not yet loaded
    // This handles the case during initial page load before getInitData() completes
    const { hostname } = window.location;
    if (hostname.includes('.eu.') || hostname.includes('-eu.') || hostname === 'kadhir.rocketlane.com' || hostname.startsWith('kadhir.') || hostname === 'testeu7.rocketlane.com' || hostname.startsWith('testeu7.')) {
        return {
            frontend: 'https://assets.marketplace.eu.rocketlane.com',
            backend: 'https://api.marketplace.eu.rocketlane.com'
        };
    }
    // Default to US region
    return {
        frontend: 'https://assets.marketplace.rocketlane.com',
        backend: 'https://api.marketplace.rocketlane.com'
    };
};
const marketplaceUrls = getMarketplaceUrls();
export const MARKETPLACE_SERVICE_FE_URL = marketplaceUrls.frontend;
export const MARKETPLACE_SERVICE_BE_URL = marketplaceUrls.backend;
export const PDFTRON_KEY = process.env.REACT_APP_PDFTRON_KEY;
export const CDN_BASE_URL = process.env.REACT_APP_ASSETS_HOST;
export const AI_STREAM_BASE_URL = process.env.REACT_APP_AI_STREAM_BASE_URL || 'https://colaslackerbot.rocketlane.com';
/**
 * Get CK_HOST based on clientRegion from accountMeta
 * If clientRegion is "eu", returns docs.eu.rocketlane.com:443
 * If clientRegion is "us" or not set, uses the existing logic (.orbits.run / .nitro.run split)
 */ export const getCKHost = ()=>{
    // Get clientRegion from localStorage accountMeta using the reusable function
    const clientRegion = getClientRegion();
    // Route based on clientRegion from backend
    if (clientRegion === 'eu') {
        return 'docs.eu.rocketlane.com:443';
    }
    // Default logic for US region or when clientRegion is not available
    if (window.location.origin.endsWith('.orbits.run') || window.location.origin.endsWith('.nitro.run') || window.location.hostname === 'localhost') {
        return 'docs-stage.nitro.run:443';
    }
    return 'docs.rocketlane.com:443';
};
export const CK_HOST = getCKHost();
export const WS_CK_HOST = CK_HOST;
export const isCKWsCloud = ()=>false;
export const AG_GRID_LICENSE_KEY = process.env.REACT_APP_AG_GRID_LICENSE_KEY;
export const DATADOG_CLIENT_TOKEN = process.env.REACT_APP_DATADOG_CLIENT_TOKEN || '';
export const DATADOG_APPLICATION_ID = process.env.REACT_APP_DATADOG_APPLICATION_ID || '';
export const DATADOG_SITE = process.env.REACT_APP_DATADOG_SITE || 'datadoghq.com';
export const VERSIONED_ASSET_LAYOUT = process.env.REACT_APP_VERSIONED_ASSET_LAYOUT === 'true';
const BUILD_ID = process.env.REACT_APP_BUILD_ID;
window.BUILD_ID = BUILD_ID || '';
if (process.env.NODE_ENV === 'production') {
    // eslint-disable-next-line no-console
    console.log('BUILD_ID', BUILD_ID);
}
export { BUILD_ID };
